import { DateTime } from 'luxon'
import { ApiErrorCode, ApiErrorParams, HttpFallbackCode, SEIRI_SUPPORT_EMAIL } from '@seiri/shared/common'
import { translatePlanLimit } from './plan.sk'

/** A plain string, or a function of the params for a code that declares params. */
type Translation<C extends ApiErrorCode> =
  ApiErrorParams<C> extends undefined ? string : (params: ApiErrorParams<C>) => string

/**
 * One entry per API error code and per status fallback. A mapped type over `API_ERRORS`: a code
 * added there is a compile error in every locale until it is translated, and each function
 * receives exactly the params its code declares.
 */
export type ErrorTranslations = { readonly [C in ApiErrorCode]: Translation<C> } & {
  readonly [C in HttpFallbackCode]: string
}

export const ERRORS_SK: ErrorTranslations = {
  'validation.failed': 'Niektoré údaje nie sú vyplnené správne.',
  'reference.notFound': () => 'Vybraná položka už neexistuje. Obnovte stránku a skúste to znova.',

  'auth.invalidCredentials': 'Nesprávny email alebo heslo.',
  'auth.emailInUse': 'Účet s týmto emailom už existuje.',
  'auth.emailDomainBlocked':
    'S e-mailom z tejto domény sa nie je možné zaregistrovať. Použite prosím inú e-mailovú adresu.',
  'account.wrongPassword': 'Súčasné heslo nie je správne.',
  'account.emailUnchanged': 'Toto je váš súčasný e-mail. Zadajte iný.',
  // The person's own date, in their own browser's zone: not a premise's.
  'account.emailChangeLimit': ({ nextAllowedAt }) =>
    `E-mail na prihlásenie ste za posledný rok zmenili už dvakrát. Ďalšia zmena bude možná od ${DateTime.fromISO(nextAllowedAt).setLocale('sk').toFormat('d. M. yyyy')}. Ak ju potrebujete skôr, napíšte nám na ${SEIRI_SUPPORT_EMAIL}.`,
  'account.emailChangeTooManyRequests': 'Dnes ste už požiadali o zmenu e-mailu viackrát. Skúste to znova zajtra.',
  'account.emailChangeLinkInvalid':
    'Odkaz na potvrdenie e-mailu už neplatí: vypršal, bol už použitý alebo ste medzitým požiadali o inú zmenu.',

  'user.cannotBlock': 'Účet správcu systému ani vlastný účet nie je možné zablokovať.',

  'customer.blocked':
    'Prevádzka vám obmedzila prihlasovanie na udalosti a vytváranie rezervácií. Pre viac informácií ju prosím kontaktujte.',
  'customer.alreadyJoined': 'V tejto prevádzke už ste zaregistrovaný.',
  'customer.notInPremise': 'Používateľ nie je klientom tejto prevádzky.',
  'customer.trialsExceeded': 'Klient už absolvoval viac skúšobných hodín.',
  'customer.emailTaken': 'Tento e-mail už má iný klient tejto prevádzky.',

  'employee.alreadyEmployee': 'Používateľ už je zamestnancom.',
  'employee.emailInUse': 'Zamestnanec s týmto emailom v prevádzke už existuje.',
  'employee.cannotDeleteOwner': 'Účet vlastníka prevádzky nie je možné vymazať.',
  'employeeInvoice.sourceRequired': 'Pre vytvorenie položky je potrebné zadať činnosť alebo udalosť.',

  'event.started': 'Udalosť už začala.',
  'event.full': 'Udalosť je plne obsadená.',
  'event.inactive': 'Udalosť nie je aktívna.',
  'event.alreadyJoined': 'Na udalosť ste už prihlásený.',
  'event.signUpUnavailable': 'Prihlásenie na udalosť teraz nie je možné dokončiť. Kontaktujte prosím prevádzku.',
  'event.cancelled': 'Udalosť je zrušená.',
  'event.ended': 'Udalosť už skončila, nedá sa upraviť.',
  'event.ongoingWithParticipants': 'Udalosť práve prebieha a má prihlásených účastníkov, nedá sa upraviť.',
  'event.timeLocked': ({ participants }) =>
    `Čas udalosti sa nedá zmeniť, sú na ňu prihlásení účastníci (${participants}).`,
  'event.notRepeating': 'Udalosť nepatrí do žiadnej série.',
  'event.alreadyInSeries': 'Udalosť už patrí do série.',
  'event.activityMismatch': 'Séria má inú činnosť ako udalosť.',
  'event.seriesEnded': 'Séria už nemá žiadne naplánované termíny.',
  'eventParticipation.eventEnded':
    'Udalosť už skončila, účastníka nie je možné odobrať. Ak neprišiel, označte ho ako „Neprišiel“.',
  'eventParticipation.eventNotStarted': 'Dochádzku je možné označiť až po začiatku udalosti.',
  'eventParticipation.eventStarted': 'Udalosť už začala, účasť sa už nedá zrušiť.',
  'eventParticipation.cancelled': 'Účasť je zrušená.',
  'eventParticipation.cancel.notAllowed': 'Túto účasť nie je možné zrušiť. Kontaktujte prosím prevádzku.',
  'eventParticipation.cancel.deadlinePassed': 'Termín na zrušenie účasti už vypršal. Kontaktujte prosím prevádzku.',
  'event.deleteUsed':
    'Udalosť, ktorá už začala alebo má účastníkov, je súčasťou histórie klientov. Odstrániť ju môže len majiteľ.',

  'reservation.slotUnavailable': 'Vybraný termín už nie je dostupný. Vyberte prosím iný.',
  'reservation.beyondHorizon': ({ months }) => `Rezervovať je možné najviac ${months} mesiacov vopred.`,
  'reservation.cancel.notAllowed': 'Túto rezerváciu nie je možné zrušiť.',
  'reservation.cancel.deadlinePassed': 'Termín na zrušenie tejto rezervácie už vypršal.',
  'reservation.started': 'Rezervácia už začala, preto ju nie je možné meniť ani zrušiť.',
  'reservation.cancelled': 'Rezervácia je zrušená.',
  'reservation.unavailable': 'Rezerváciu teraz nie je možné dokončiť. Kontaktujte prosím prevádzku.',
  'reservation.deleteUsed':
    'Rezervácia s klientom, zrušená alebo už začatá je súčasťou histórie klienta. Zrušte ju, odstrániť ju môže len majiteľ.',

  'billing.disabled': 'Táto prevádzka má trvalý bezplatný prístup, predplatné nie je potrebné.',
  'billing.contactSales': 'Plán Na mieru dohodneme individuálne.',
  'billing.priceNotConfigured': ({ itemName }) => `${itemName} zatiaľ nie je možné zakúpiť.`,
  'billing.addonRequiresPlan': 'Doplnky sú dostupné až s plateným plánom.',
  'sms.creditsInsufficient': ({ balance, requested }) =>
    `Nedostatok SMS kreditov: potrebných ${requested}, zostáva ${balance}.`,

  'image.unsupportedFormat': 'Nahrať je možné len obrázok vo formáte JPEG, PNG alebo WebP.',
  'image.tooLarge': ({ maxMegabytes }) => `Obrázok je príliš veľký. Nahrať je možné súbor do ${maxMegabytes} MB.`,
  'image.tooManyPixels': ({ maxMegapixels }) =>
    `Obrázok má príliš vysoké rozlíšenie. Nahrať je možné obrázok do ${maxMegapixels} megapixelov.`,
  'image.tooSmall': ({ minWidth }) => `Obrázok je príliš malý. Musí mať šírku aspoň ${minWidth} px.`,
  'image.galleryFull': ({ limit }) => `Galéria je plná. Môže obsahovať najviac ${limit} obrázkov.`,

  'plan.limit.count': (params) => translatePlanLimit('plan.limit.count', params),
  'plan.limit.quota': (params) => translatePlanLimit('plan.limit.quota', params),
  'plan.limit.feature': (params) => translatePlanLimit('plan.limit.feature', params),

  'http.badRequest': 'Požiadavku sa nepodarilo spracovať.',
  'http.unauthorized': 'Prihláste sa prosím znova.',
  'http.forbidden': 'Na túto akciu nemáte oprávnenie.',
  'http.notFound': 'Požadovaná položka nebola nájdená.',
  'http.conflict': 'Akciu nie je možné vykonať v aktuálnom stave. Obnovte stránku a skúste to znova.',
  'http.tooManyRequests': 'Príliš veľa pokusov. Skúste to prosím o chvíľu.',
  'http.serverError': 'Nastala chyba na serveri. Skúste to prosím neskôr.',
  'network.failed': 'Nepodarilo sa spojiť so serverom. Skontrolujte pripojenie a skúste to znova.',
}
